'use client'

import { CreditCard, Settings, ShieldCheck } from 'lucide-react'

import { logSearchResultClicked, logSearchResultViewed } from '@/app/(marketplace)/plugins/actions'
import { useGlobalSearch } from '@/components/provider/global-search-provider'
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/components/ui/command'
import { isMarketplaceEnabled } from '@/lib/flags'
import {
  SearchResult,
  searchBlogArticles,
  searchContentPages,
  searchDocs,
  searchPlugins,
} from '@/lib/search'
import { useQuery } from '@tanstack/react-query'
import { debounce } from 'lodash'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { RefObject, useEffect, useRef } from 'react'
import { useOnClickOutside } from 'usehooks-ts'
import { DialogTitle } from '../ui/dialog'

function GlobalSearchResultsGroup({
  heading,
  results = [],
  isLoading = false,
  onItemClick,
  onItemViewed,
}: {
  heading: string
  results: SearchResult[]
  isLoading?: boolean
  onItemClick?: (result: SearchResult, position: number) => void
  onItemViewed?: (result: SearchResult, position: number) => void
}) {
  return (
    <CommandGroup heading={heading}>
      {!isLoading &&
        results?.map((result, index) => (
          <CommandItem
            asChild
            key={result.url + result.label}
            className="cursor-pointer"
            onMouseEnter={() => {
              onItemViewed?.(result, index)
            }}
            onClick={() => {
              onItemClick?.(result, index)
            }}
          >
            <Link href={result.url}>{result.label}</Link>
          </CommandItem>
        ))}
    </CommandGroup>
  )
}

export function GlobalSearch() {
  const { searchOpen, query, onOpenSearch, setQuery } = useGlobalSearch()
  const ref = useRef(null)
  const pathname = usePathname()

  useEffect(() => {
    onOpenSearch(false)
  }, [pathname])

  const contentPagesQuery = useQuery({
    queryKey: ['global-search', 'content-pages', query],
    queryFn: () => searchContentPages(query),
    enabled: query.length > 0,
  })

  const blogArticlesQuery = useQuery({
    queryKey: ['global-search', 'blog-articles', query],
    queryFn: () => searchBlogArticles(query),
    enabled: query.length > 0,
  })

  const pluginsQuery = useQuery({
    queryKey: ['global-search', 'plugins', query],
    queryFn: () => searchPlugins(query),
    enabled: query.length > 0,
  })

  const docsQuery = useQuery({
    queryKey: ['global-search', 'docs', query],
    queryFn: () => searchDocs(query),
    enabled: query.length > 0,
  })

  useOnClickOutside(ref as unknown as RefObject<HTMLDivElement>, () => {
    onOpenSearch(false)
  })

  return (
    <CommandDialog
      open={searchOpen}
      onOpenChange={onOpenSearch}
      commandProps={{
        shouldFilter: false,
      }}
    >
      <DialogTitle className="hidden">Search</DialogTitle>
      <CommandInput
        placeholder="Type a command or search..."
        defaultValue={query}
        onValueChange={debounce((value: string) => setQuery(value), 300)}
      />
      <CommandList>
        <GlobalSearchResultsGroup
          heading="Content Pages"
          results={contentPagesQuery.data}
          isLoading={contentPagesQuery.isLoading}
        />

        <GlobalSearchResultsGroup
          heading="Blog Articles"
          results={blogArticlesQuery.data}
          isLoading={blogArticlesQuery.isLoading}
        />

        <GlobalSearchResultsGroup
          heading="Plugins"
          results={pluginsQuery.data}
          isLoading={pluginsQuery.isLoading}
          onItemClick={(result, position) => {
            if (result.queryId) {
              logSearchResultClicked(result.queryId, result.id, position)
            }
          }}
          onItemViewed={(result, position) => {
            if (result.queryId) {
              logSearchResultViewed(result.queryId)
            }
          }}
        />

        <GlobalSearchResultsGroup
          heading="Docs"
          results={docsQuery.data}
          isLoading={docsQuery.isLoading}
        />

        {isMarketplaceEnabled && (
          <CommandGroup heading="Actions">
            <CommandItem>
              <ShieldCheck />
              <span>Licenses</span>
              <CommandShortcut>⌘P</CommandShortcut>
            </CommandItem>
            <CommandItem>
              <CreditCard />
              <span>Billing</span>
              <CommandShortcut>⌘B</CommandShortcut>
            </CommandItem>
            <CommandItem>
              <Settings />
              <span>Settings</span>
              <CommandShortcut>⌘S</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
